import React from 'react';
import { Card, CardContent, CardHeader, styled } from '@mui/material';

export type InsightCardVariant = 'default' | 'primary' | 'success' | 'warning' | 'error';

interface InsightCardProps {
  children: React.ReactNode;
  cardVariant?: InsightCardVariant;
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  elevation?: number;
}

const StyledCard = styled(Card)<{ $cardVariant: InsightCardVariant }>(({ theme, $cardVariant }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: theme.shadows[4],
  },
  
  // Variant-specific styling
  ...($cardVariant === 'primary' && {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  }),
  ...($cardVariant === 'success' && {
    borderLeft: `4px solid ${theme.palette.success.main}`,
  }),
  ...($cardVariant === 'warning' && {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  }),
  ...($cardVariant === 'error' && {
    borderLeft: `4px solid ${theme.palette.error.main}`,
  }),
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  '&:last-child': {
    paddingBottom: theme.spacing(2),
  },
  
  // Consistent spacing for content
  '& > *:not(:last-child)': {
    marginBottom: theme.spacing(1.5),
  },
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  paddingBottom: theme.spacing(1),
  '& .MuiCardHeader-title': {
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.h6.fontWeight,
    color: theme.palette.text.primary,
  },
  '& .MuiCardHeader-subheader': {
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

const InsightCard: React.FC<InsightCardProps> = ({
  children,
  cardVariant = 'default',
  title,
  subtitle,
  action,
  elevation = 1,
}) => {
  return (
    <StyledCard $cardVariant={cardVariant} elevation={elevation}>
      {(title || subtitle || action) && (
        <StyledCardHeader
          title={title}
          subheader={subtitle}
          action={action}
        />
      )}
      <StyledCardContent>
        {children}
      </StyledCardContent>
    </StyledCard>
  );
};

export default InsightCard;